import React from 'react';

/**
 * GPSStatusBadge - Shows GPS availability and accuracy status
 * @param {Object} props
 * @param {boolean} props.isGPSAvailable - Whether GPS is available
 * @param {boolean} props.hasLocation - Whether location was captured
 * @param {number} props.accuracy - GPS accuracy in meters (optional)
 * @param {boolean} props.isLoading - Whether GPS is being captured
 */
function GPSStatusBadge({ isGPSAvailable, hasLocation, accuracy, isLoading }) {
  if (isLoading) {
    return (
      <div className="inline-flex items-center px-2 py-1 rounded-md text-xs text-blue-600 bg-blue-50 border border-blue-200">
        <svg className="animate-spin h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        Getting location...
      </div>
    );
  }

  if (!isGPSAvailable) {
    return (
      <div className="inline-flex items-center px-2 py-1 rounded-md text-xs text-red-600 bg-red-50 border border-red-200" title="GPS is not available. Please enable location services in your device settings.">
        <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
        GPS unavailable
      </div>
    );
  }

  if (!hasLocation) {
    return (
      <div className="inline-flex items-center px-2 py-1 rounded-md text-xs text-gray-600 bg-gray-50 border border-gray-200">
        <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        Location not captured
      </div>
    );
  }

  // Show accuracy status
  const isAccurate = accuracy && accuracy <= 50; // Good accuracy
  const isModerate = accuracy && accuracy > 50 && accuracy <= 100; // Moderate accuracy
  const isPoor = accuracy && accuracy > 100; // Poor accuracy

  let colorClass = 'text-green-600 bg-green-50 border-green-200';
  let accuracyText = 'Good';

  if (isModerate) {
    colorClass = 'text-yellow-600 bg-yellow-50 border-yellow-200';
    accuracyText = 'Moderate';
  } else if (isPoor) {
    colorClass = 'text-orange-600 bg-orange-50 border-orange-200';
    accuracyText = 'Poor';
  }

  return (
    <div className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${colorClass}`}>
      <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      Location captured {accuracy && `(${accuracyText} ±${accuracy}m)`}
    </div>
  );
}

export default GPSStatusBadge;
